import { DomainFault, type DomainModule } from '../contracts';
import type { Role } from '../types';
import { profileFor } from './learning/learning';

const operators: Role[] = [
  'groupOperator',
  'regionManager',
  'campusManager',
  'support',
  'deviceManager',
];
const reviewers: Role[] = ['groupOperator', 'regionManager', 'campusManager'];
const all: Role[] = [
  'groupLeader',
  'groupOperator',
  'regionManager',
  'campusManager',
  'teacher',
  'researchLead',
  'reviewer',
  'studentService',
  'deviceManager',
  'systemAdmin',
  'support',
];
const required = (value: string | undefined, name: string) => {
  if (!value?.trim()) throw new DomainFault('VALIDATION', `请填写${name}`);
  return value.trim();
};
export const supportDomain: DomainModule = {
  namespace: 'support',
  version: 2,
  resource: 'help',
  migrations: [
    {
      version: 1,
      up(state) {
        state.domainData ||= {};
        state.domainData.support ||= { training: [] };
      },
    },
    {
      version: 2,
      up(state) {
        const guides = [
          [
            'recording-check',
            '录音未生成的排查步骤',
            '设备操作',
            '1. 核对工牌是否已领用及发生时的人员绑定。\n2. 查看设备型号已验证的采集能力。\n3. 在采集控制台核对文件传输与后台确认，单个失败文件可独立重试。\n4. 在录音中心确认业务归属，信息缺失时保留待确认。\n5. 仍未解决时关联设备并提交服务工单，不删除设备源文件。',
          ],
          [
            'access-check',
            '录音与线索不可见时如何核对',
            '录音管理',
            '1. 核对当前业务角色与校区范围。\n2. 确认内容权限和发生时绑定，而不是仅检查菜单是否可见。\n3. 线索、片段与助手引用继承来源权限，撤权后不应继续展示正文。\n4. 系统管理员默认没有录音内容访问权。\n5. 请由授权负责人核实业务策略，真实平台权限在企业管理中维护。',
          ],
          [
            'recording-loop',
            '从录音证据到事项验收',
            '业务流程',
            '1. 领用设备并核实绑定时间。\n2. 采集文件并等待后台确认。\n3. 回听音频与转写，保留准确片段及版本。\n4. 模拟分析生成候选，由人工复核确认。\n5. 为成立线索创建事项，明确负责人、期限和必要任务。\n6. 提交结果并验收，必要任务未完成时不可关闭。\n7. 重开保留前轮处理及验收记录。',
          ],
          [
            'teaching-method',
            '课堂观察、实践与案例整理',
            '培训',
            '1. 在课次中核实录音关联和章节角色。\n2. 根据原始片段记录并人工确认教学观察。\n3. 制定可观察的目标和实践完成条件。\n4. 提交另一课堂的独立录音与场景差异，由指定导师核实。\n5. 整理案例时核对来源、脱敏和可读范围，由另一位审核人发布。\n6. 阅读、收藏、引用和实践验收分别统计，不生成无依据的提升或排名。',
          ],
        ];
        const campuses = [
          ...new Set(
            state.entities
              .filter((e) => e.kind === 'teacher')
              .map((e) => e.campus),
          ),
        ];
        campuses.forEach((campus, ci) => {
          guides.forEach(([code, title, category, body], gi) => {
            if (
              state.entities.some(
                (e) =>
                  e.kind === 'help' &&
                  e.campus === campus &&
                  e.fields?.supportGuideKey === code,
              )
            )
              return;
            state.entities.push({
              uuid: `30000000-0000-4000-8000-${String(1000 + ci * 100 + gi).padStart(12, '0')}`,
              kind: 'help',
              title,
              campus,
              status: 'published',
              memberUuid: '',
              note: '系统使用指南，适用于本地演示业务流程。',
              revision: 1,
              createdAt: state.now,
              updatedAt: state.now,
              fields: { supportGuideKey: code, category, body },
            });
          });
        });
      },
    },
  ],
  query(ctx) {
    const training = (ctx.data().training || []) as unknown as {
      memberUuid: string;
      helpUuid: string;
      revision: number;
      at: string;
      note: string;
    }[];
    return {
      training: training.filter(
        (row) => row.memberUuid === ctx.context.viewMemberUuid,
      ),
    };
  },
  commands: {
    assign: {
      roles: operators,
      kind: 'ticket',
      label: '分派服务工单',
      execute(ctx, input) {
        const row = ctx.get(input.uuid);
        if (
          row?.kind !== 'ticket' ||
          ['closed', 'cancelled'].includes(row.status)
        )
          throw new DomainFault('INVALID_TRANSITION', '当前工单不能分派');
        const owner = ctx
          .members()
          .find(
            (m) => m.memberUuid === input.memberUuid && m.campus === row.campus,
          );
        if (!owner)
          throw new DomainFault('VALIDATION', '负责人须为同校区的授权成员');
        row.owner = owner.memberUuid;
        row.note = required(input.note, '分派依据');
        row.status = 'inProgress';
        ctx.touch(row);
        ctx.task(row, `处理服务工单：${row.title}`);
        ctx.notify(row, '服务工单已分派');
        return row;
      },
    },
    resolve: {
      roles: operators,
      kind: 'ticket',
      label: '提交工单解决方案',
      execute(ctx, input) {
        const row = ctx.get(input.uuid);
        if (
          row?.kind !== 'ticket' ||
          !['inProgress', 'reopened'].includes(row.status)
        )
          throw new DomainFault('INVALID_TRANSITION', '请先接单或重新打开工单');
        const result = required(input.note, '处理过程、结果与验证依据');
        if (result.length < 12)
          throw new DomainFault('VALIDATION', '请补充可核实的处理过程与结果');
        row.fields = {
          ...row.fields,
          resolution: result,
          resolvedBy: ctx.context.viewMemberUuid,
          resolvedAt: ctx.now,
        };
        row.note = result;
        row.status = 'pendingAcceptance';
        ctx.touch(row);
        ctx.task(row, `验收服务工单：${row.title}`);
        ctx.notify(row, '工单解决方案待验收');
        return row;
      },
    },
    accept: {
      roles: reviewers,
      kind: 'ticket',
      label: '验收关闭服务工单',
      execute(ctx, input) {
        const row = ctx.get(input.uuid);
        if (
          row?.kind !== 'ticket' ||
          row.status !== 'pendingAcceptance' ||
          !row.fields?.resolution
        )
          throw new DomainFault('INVALID_TRANSITION', '缺少待验收解决方案');
        if (row.fields.resolvedBy === ctx.context.viewMemberUuid)
          throw new DomainFault('FORBIDDEN', '请由另一位负责人核实验收');
        row.note = required(input.note, '验收依据');
        row.fields = {
          ...row.fields,
          acceptance: row.note,
          acceptedBy: ctx.context.viewMemberUuid,
          acceptedAt: ctx.now,
        };
        row.status = 'closed';
        ctx.touch(row);
        for (const task of ctx
          .list('task')
          .filter(
            (t) => t.sourceUuid === row.uuid && t.status !== 'completed',
          )) {
          task.status = 'completed';
          ctx.touch(task);
        }
        ctx.notify(row, '服务工单已验收关闭');
        return row;
      },
    },
    reopen: {
      roles: reviewers,
      kind: 'ticket',
      label: '重新打开服务工单',
      execute(ctx, input) {
        const row = ctx.get(input.uuid);
        if (row?.kind !== 'ticket' || row.status !== 'closed')
          throw new DomainFault('INVALID_TRANSITION', '只有已关闭工单可重开');
        row.note = required(input.note, '重开原因');
        row.status = 'reopened';
        ctx.touch(row);
        ctx.task(row, `重新处理工单：${row.title}`);
        ctx.notify(row, '服务工单已重新打开');
        return row;
      },
    },
    learn: {
      roles: all,
      kind: 'help',
      label: '登记指南学习反馈',
      execute(ctx, input) {
        const row = ctx.get(input.uuid);
        if (row?.kind !== 'help' || row.status !== 'published')
          throw new DomainFault('INVALID_TRANSITION', '只可登记已发布指南');
        const note = required(input.note, '学习反馈');
        const data = structuredClone(ctx.data());
        const training = (data.training || []) as unknown as {
          memberUuid: string;
          helpUuid: string;
          revision: number;
          at: string;
          note: string;
        }[];
        if (
          training.some(
            (t) =>
              t.memberUuid === ctx.context.viewMemberUuid &&
              t.helpUuid === row.uuid &&
              t.revision === row.revision,
          )
        )
          return row;
        training.push({
          memberUuid: ctx.context.viewMemberUuid,
          helpUuid: row.uuid,
          revision: row.revision,
          at: ctx.now,
          note,
        });
        ctx.setData({ ...data, training: training as never });
        const teacher = ctx
          .list('teacher')
          .find((t) => t.memberUuid === ctx.context.viewMemberUuid);
        if (teacher) {
          const profile = profileFor(teacher);
          profile.training.push({
            uuid: ctx.commandUuid,
            title: row.title,
            at: ctx.now,
            feedback: `指南修订 ${row.revision}：${note}`,
          });
          teacher.fields = {
            ...teacher.fields,
            learningProfile: JSON.stringify(profile),
          };
          ctx.touch(teacher);
        }
        return row;
      },
    },
  },
};
